import type { BackendTask } from '@modules/background-tasks';

export type TaskPostArtifactStorage = 'content_posts' | 'post_generation_draft';

export interface TaskPostArtifactRef {
  artifactType: 'content_post';
  postId: string;
  projectId: string;
  topicId?: string;
  platform?: string;
  format?: string;
  storage: TaskPostArtifactStorage;
  firestorePath: string;
  editableUrl: string;
  previewText?: string;
  createdByTaskId?: string;
  runId?: string;
}

interface TaskLike {
  id: string;
  taskType?: string;
  result?: unknown;
  projectId?: string;
  strategyId?: string;
  request?: unknown;
  targetEntityPath?: { frontendUrl?: string };
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function fallbackEditableUrl(task: TaskLike, projectId: string, topicId?: string): string {
  if (topicId) {
    return `/project/${projectId}/content-plan?cpTab=backlog&topic=${encodeURIComponent(topicId)}`;
  }
  if (task.targetEntityPath?.frontendUrl) return task.targetEntityPath.frontendUrl;
  if (task.strategyId) {
    return `/project/${projectId}/content-plan?cpTab=strategies&strategy=${encodeURIComponent(task.strategyId)}`;
  }
  return `/project/${projectId}/content-plan`;
}

export function publicationPathForTaskPostArtifactFallback(task: TaskLike | BackendTask | null | undefined): string | null {
  if (!task || task.taskType !== 'content-post-generation') return null;
  const target = (task as TaskLike).targetEntityPath as { entityType?: string; firestorePath?: string } | undefined;
  if (target?.entityType === 'content_publication' && readString(target.firestorePath)) {
    return readString(target.firestorePath) ?? null;
  }
  const projectId = readString((task as TaskLike).projectId);
  const request = readRecord((task as TaskLike).request);
  const slotAssignment = readRecord(request?.slotAssignment);
  const strategyId = readString((task as TaskLike).strategyId) ?? readString(slotAssignment?.strategyId);
  const publicationId = readString(request?.publicationId) ?? readString(slotAssignment?.publicationId);
  if (!projectId || !strategyId || !publicationId) return null;
  return `projects/${projectId}/content_strategies/${strategyId}/publications/${publicationId}`;
}

export function hasPublicationPostArtifactFallback(task: TaskLike | BackendTask | null | undefined): boolean {
  return Boolean(publicationPathForTaskPostArtifactFallback(task));
}

export function normalizeTaskPostArtifactRefs(task: TaskLike | BackendTask | null | undefined): TaskPostArtifactRef[] {
  if (!task || task.taskType !== 'content-post-generation') return [];
  const projectId = readString((task as TaskLike).projectId);
  const result = readRecord(task.result);
  const rawArtifacts = Array.isArray(result?.artifacts) ? result.artifacts : [];
  return rawArtifacts.flatMap((item) => {
    const raw = readRecord(item);
    if (!raw || raw.artifactType !== 'content_post') return [];
    const postId = readString(raw.postId);
    const artifactProjectId = readString(raw.projectId) ?? projectId;
    const firestorePath = readString(raw.firestorePath);
    const storage = raw.storage === 'post_generation_draft' ? 'post_generation_draft' : 'content_posts';
    if (!postId || !artifactProjectId || !firestorePath) return [];
    const topicId = readString(raw.topicId);
    return [{
      artifactType: 'content_post',
      postId,
      projectId: artifactProjectId,
      topicId,
      platform: readString(raw.platform),
      format: readString(raw.format),
      storage,
      firestorePath,
      editableUrl: readString(raw.editableUrl) ?? fallbackEditableUrl(task, artifactProjectId, topicId),
      previewText: readString(raw.previewText),
      createdByTaskId: readString(raw.createdByTaskId),
      runId: readString(raw.runId) ?? (storage === 'post_generation_draft' ? task.id : undefined),
    }];
  });
}

export function legacyDraftRunIdForTask(task: TaskLike | BackendTask | null | undefined): string | null {
  if (!task || task.taskType !== 'content-post-generation') return null;
  return normalizeTaskPostArtifactRefs(task).length === 0 ? task.id : null;
}
